import {
  ChangeEvent,
  FC,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Alert,
  AlertColor,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  deleteDocumentationForm,
  setDocumentationForm,
  updateDocumentationForm,
} from "../../api/users-patients";
import { UserProgramContext } from "../../context/user-program/user-program";
import { DetailDocumentation } from "./detail-documentation";
import { IDocumentation } from "../../interface/IDocument";
import { useLocation } from "react-router-dom";
import { PsychologyTest } from "./psychology-test-page";
import { NutritionalPage } from "./nutritional-therapy-page";
import { FilePage } from "./file/file-page";
import { TerminhistoriePage } from "./appointment-history";

export const DocumentationPage: FC<IDocumentation> = ({
  patientId,
  documentation,
  documentationUpdate,
  detailPatient,
}) => {
  const { userPayload } = useContext(UserProgramContext);
  const [selectedValue, setSelectedValue] = useState("Kategorie");
  const [textareaValue, setTextareaValue] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);

  const [selectedDocumentationId, setSelectedDocumentationId] = useState<
    string | null
  >(null);

  const pageTabRender = [
    "testergebnisse",
    "ernährungstherapie",
    "dateien",
    "upload-file",
    "terminhistorie",
  ];

  const { _id, firstName, lastName } = userPayload as {
    _id: string;
    firstName: string;
    lastName: string;
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryParam = queryParams.get("filter") || "";

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedValue(event.target.value);
  };

  const handleTextareaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaValue(event.target.value);
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (selectedDocumentationId && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [selectedDocumentationId, setLoading]);

  const handleAddDocumentation = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    try {
      setLoading(true);
      const documentationData = {
        title: "",
        description: textareaValue,
        category: selectedValue,
        patient: patientId,
        employee: _id,
      };

      const updateData = {
        description: textareaValue,
        category: selectedValue,
      };

      const response = selectedDocumentationId
        ? await updateDocumentationForm(selectedDocumentationId, updateData)
        : await setDocumentationForm(documentationData);

      const successMessage = `Der Dokumentation  wurde erfolgreich ${
        selectedDocumentationId ? "aktualisiert" : "erstellt"
      }`;

      documentationUpdate((prevDocumentation) =>
        selectedDocumentationId
          ? prevDocumentation.map((doc) =>
              doc._id === selectedDocumentationId ? response.data : doc
            )
          : [...prevDocumentation, response.data]
      );

      setSnackbarOpen(true);
      setSnackbarMessage(successMessage);
      setSnackbarSeverity("success");
      setLoading(false);
    } catch (error) {
      const errorMessage = `Fehler beim ${
        selectedDocumentationId ? "Aktualisieren" : "Erstellen"
      } des  Dokumentation`;

      console.error(errorMessage, error);

      setSnackbarOpen(true);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setLoading(false);
    }

    setSelectedValue("Kategorie");
    setTextareaValue("");
    setSelectedDocumentationId(null);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await deleteDocumentationForm(id);

      documentationUpdate((prevDocumentation) =>
        prevDocumentation.filter((doc) => doc._id !== id)
      );
      setSnackbarOpen(true);
      setSnackbarMessage("Die Nachricht wurde erfolgreich gelöscht");
      setLoading(false);
    } catch (error) {
      console.error("Error", error);
    }
  };

  return (
    <Grid container>
      {pageTabRender.includes(categoryParam) ? (
        <Fragment>
          {categoryParam === "testergebnisse" ? (
            <PsychologyTest />
          ) : categoryParam === "ernährungstherapie" ? (
            <NutritionalPage detailPatient={detailPatient} />
          ) : categoryParam === "dateien" ? (
            <FilePage />
          ) : categoryParam === "terminhistorie" ? (
            <TerminhistoriePage />
          ) : null}
        </Fragment>
      ) : (
        <Grid container mt={5} sx={{ height: "350px" }}>
          <Grid item xs={12}>
            <Typography variant="h5" component="h5" fontWeight={600}>
              Neue Dokumentation hinzufügen
            </Typography>
          </Grid>
          <FormControl
            component="form"
            onSubmit={handleAddDocumentation}
            fullWidth
          >
            <Grid container mt={2} spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled
                  defaultValue={` ${firstName} ${lastName}`}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Select
                  labelId="select-label"
                  id="select"
                  defaultValue="Kategorie"
                  value={selectedValue}
                  onChange={handleSelectChange}
                  variant="outlined"
                  fullWidth
                  inputRef={textAreaRef}
                >
                  <MenuItem value="Kategorie" disabled>
                    Kategorie
                  </MenuItem>
                  <MenuItem value="allgemein">Allgemein</MenuItem>
                  <MenuItem value="wichtig">Wichtig</MenuItem>
                  <MenuItem value="psy-gutachten">Psy. Gutachten</MenuItem>
                  <MenuItem value="ernährungstherapie">
                    Ernährungstherapie
                  </MenuItem>
                  <MenuItem value="verhaltenstraining">
                    Verhaltenstraining
                  </MenuItem>
                  <MenuItem value="sporttherapie">Sporttherapie</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  id="outlined-multiline-flexible"
                  label="Hier schreiben..."
                  multiline
                  rows={10}
                  variant="outlined"
                  fullWidth
                  sx={{}}
                  value={textareaValue}
                  onChange={handleTextareaChange}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box width="100%" display="flex" justifyContent="end">
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={selectedValue === "Kategorie"}
                  >
                    {selectedDocumentationId ? "Aktualisieren" : "Hinzufügen"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormControl>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
          >
            <Alert
              variant="filled"
              severity={snackbarSeverity as AlertColor | undefined}
              onClose={handleSnackbarClose}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Grid>
      )}
      {loading ? (
        <Grid
          item
          xs={12}
          sm={12}
          mt="250px"
          display="flex"
          alignContent="center"
          justifyContent="center"
          height="500px"
        >
          <CircularProgress size={100} />
        </Grid>
      ) : (
        <DetailDocumentation
          patientId={patientId as string}
          documentation={documentation}
          handleDelete={handleDelete}
          onSelectDocumentation={(docId: string) => {
            setSelectedDocumentationId(docId);
            const selectedDoc = documentation.find((doc) => doc._id === docId);
            if (selectedDoc) {
              setSelectedValue(selectedDoc.category || "");
              setTextareaValue(selectedDoc.description || "");
            }
          }}
        />
      )}
    </Grid>
  );
};
